import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {filter, map} from 'rxjs/operators';
import {ReplaySubject, Subscription} from 'rxjs';
import {Block, BlockType, Input as InputBlock} from '@paperlessio/sdk/api/models';
import {BlockStore} from './block.store';
import {SessionStore} from '../../submission/src/app/session/session.store';
import {EditorRoutingService} from '@management/editor/editor-routing.service';
import {BLOCKS_CONFIG} from '../blocks.config';
import {BlockChangeDetectionService} from '@blocks/services/block-change-detection/block-change-detection.service';
import {BlockMutationService} from '@blocks/services/block-mutation/block-mutation.service';
import {DataSetInputRowComponent} from '@blocks/input/data-set-input/data-set-input-row/data-set-input-row.component';
import {Router} from '@angular/router';

@Component({
  selector: 'app-block',
  templateUrl: './block.component.html',
  styleUrls: ['./block.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BlockComponent implements OnInit, OnDestroy {
  blocksConfig = inject(BLOCKS_CONFIG);

  private editorRoutingService = inject(EditorRoutingService, {optional: true});
  private router = inject(Router);
  private blockStore = inject(BlockStore);
  private blockDetectionService = inject(BlockChangeDetectionService);
  private blockMutationService = inject(BlockMutationService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private sessionStore = inject(SessionStore);

  @Input() block: Block;

  repaints = 0;
  debug = false;
  BlockType = BlockType;

  public blockChanged = new ReplaySubject<Block>(1);

  get data() {
    const inputBlock = this.block as InputBlock;

    if (this.blocksConfig.editable) {
      return inputBlock.default_value;
    } else {
      const block_data = this.sessionStore?.getBlockData(this.virtualSlug);
      const default_value = inputBlock.defaultValueForSlug(this.virtualSlug);
      return block_data ?? default_value;
    }
  }

  set data(data: any) {
    if (this.blocksConfig.editable) {
      (this.block as InputBlock).default_value = data;
      this.blockMutationService.updateBlock(this.block);
    }
  }

  private subs = new Subscription();

  @HostListener('click', ['$event'])
  async click(event) {
    if (this.blocksConfig.selectable &&
      !!this.editorRoutingService
      && this.block.type !== BlockType.Row
      && this.block.type !== BlockType.PdfPart) {
      if (this.block.type !== BlockType.Page) {
        if (this.block.type === BlockType.Column) {
          await this.editorRoutingService.navigateToBlock(this.block.parent_id);
        } else if (this.editorRoutingService.selectedBlock !== this.block.id
          || this.editorRoutingService.selectedPanel) {
          await this.editorRoutingService.navigateToBlock(this.block.id);
        }
      } else {
        await this.editorRoutingService.navigateToLastActivePanel();
      }

      event.stopPropagation();
    }
  }

  @HostListener('dblclick', ['$event'])
  async dblclick(event) {
    if (this.blocksConfig.selectable && this.blocksConfig.editable && this.blocksConfig.submittable && !this.blocksConfig.preview) {
      switch (this.block.type) {
        case BlockType.RadioInput:
        case BlockType.CheckboxInput:
          // open options modal
          await this.router.navigate(['', {outlets: {modal: 'm/select_options/' + this.block.id}}]);
          break;
        default:
          // do nothing
      }
    }
  }

  @HostBinding('class.pdf-page') get pdfPage(): boolean {
    return this.block.children?.[0]?.type === BlockType.PdfPart || this.block.type === BlockType.PdfPart;
  }

  @HostBinding('class.selected-block') get selected(): boolean {
    return this.blocksConfig.selectable && this.editorRoutingService?.blockSelected(this.block);
  }

  @HostBinding('class.d-print-none') get hideInExport(): boolean {
    return this.block.settings.hideInExport;
  }

  ngOnInit() {
    this.subs.add(this.blockChanged.subscribe(() => this.changeDetectorRef.markForCheck()));
    this.subscribeToBlockChanges();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  test() {
    return ++this.repaints;
  }

  dataSetInputRowComponent = inject(DataSetInputRowComponent, {skipSelf: true, optional: true});

  get virtualSlug() {
    // TODO: memoize
    const parentSlug = this.dataSetInputRowComponent?.virtualSlug ?? [];
    return parentSlug.length ? [...parentSlug, this.block.slug].join('.') : this.block.slug;
  }

  // to avoid multiple change detection cycles we trigger change detection manually
  // when the block is in the list of changed blocks
  private subscribeToBlockChanges() {
    this.subs.add(
      this.blockDetectionService.changedBlockIds$
        .pipe(
          filter((blockId: number) => blockId === this.block.id),
          map(id => this.blockStore.getSync(id))
        )
        .subscribe(block => this.blockChanged.next(block))
    );
  }
}
